import { useState, useEffect } from "react";
import { AppDrawer, EmptyResult, MainContent, MainLayout, MainSidebar, SearchBar } from "components";
import { Stack, List } from "@mui/material";
import { getInteractor } from "services/local.service";
import { useTranslation } from "react-i18next";
import { Apps } from "@mui/icons-material";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import {
  DEFAULT_FILTER,
  FileFilter,
  FILTER_FILE_TYPE,
  OTHER_FILE_TYPES,
  PAGING_SIZE,
  TimeFilter,
} from "./components/FileFilter";
import { Container, FileListLoading, FileTabButton, LimitContainer, ReplyIcon } from "./components";
import { ListFileByDate } from "./components/FileList";
import { AppConstant, LangConstant } from "const";
import { StorageUtil } from "utils";
import Scrollable from "./components/Scrollable";
import { SystemActions } from "redux-store";
import { useDispatch } from "react-redux";
import { groupDataByDate } from "utils/date.utils";
import { refactorFiles } from "./file.helper";

const ManageFilePage = () => {
  const dispatch = useDispatch();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const accountObjContent = getLabel(LangConstant.OBJ_ACCOUNT_ME, { returnObjects: true });
  const prefixKey = StorageUtil.getCurrentPrefixKey();

  const [viewMode, setViewMode] = useState(AppConstant.VIEW_MODE.fetching);
  const [selectedTab, setSelectedTab] = useState(FILE_TAB.all);
  const [filterCondition, setFilterCondition] = useState(DEFAULT_FILTER);
  const [searchValue, setSearchValue] = useState("");
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [fileList, setFileList] = useState({}); // List data by date
  const [displayItemTotal, setDisplayItemTotal] = useState(0);

  const handleOpenHomePage = () => {
    dispatch(
      SystemActions.systemSet({
        isShowMnFile: false,
      }),
    );
  };
  const handleChangeFilter = condition => setFilterCondition(preState => ({ ...preState, ...condition }));
  const handleSearch = keyword => setSearchValue(keyword);
  const handleFilterByTime = (startDate, endDate) => handleChangeFilter({ startDate, endDate });

  const handleSelectTabFunc = tabValue => () => {
    setSelectedTab(tabValue);

    switch (tabValue) {
      case FILE_TAB.all:
        handleChangeFilter({ isAll: true, createdByMe: false, createdByAccountIds: null, offset: 0 });
        break;

      case FILE_TAB.mine:
        handleChangeFilter({ isAll: false, createdByMe: true, createdByAccountIds: null, offset: 0 });
        break;

      case FILE_TAB.other:
        handleChangeFilter({ isAll: false, createdByMe: false, createdByAccountIds: null, offset: 0 });
        break;

      default:
        break;
    }
  };

  const handleFilterByType = fileTypes => {
    switch (true) {
      // View all file types
      case [Object.values(FILTER_FILE_TYPE).length, 0].includes(fileTypes.length):
        handleChangeFilter({ fileType: null });
        break;

      // Select other file
      case fileTypes.includes(FILTER_FILE_TYPE.otherFile):
        handleChangeFilter({ fileType: fileTypes.concat(OTHER_FILE_TYPES) });
        break;

      default:
        handleChangeFilter({ fileType: fileTypes });
        break;
    }
  };

  const handleLoadMore = async () => {
    if (!isLoadMore) return;

    const records = await getInteractor(prefixKey).LocalFileGroupService.searchFileByCondition({
      keyword: searchValue,
      ...filterCondition,
      offset: displayItemTotal,
    });
    setIsLoadMore(records.length === PAGING_SIZE);
    const refactorFileList = await refactorFiles(prefixKey, records);

    const result = groupDataByDate(refactorFileList, fileList);
    setFileList(result);
    setDisplayItemTotal(displayItemTotal + records.length);
  };

  useEffect(() => {
    setViewMode(AppConstant.VIEW_MODE.fetching);

    const getFileTimeout = setTimeout(() => {
      getInteractor(prefixKey)
        .LocalFileGroupService.searchFileByCondition({
          keyword: searchValue,
          ...filterCondition,
          offset: 0,
          limit: PAGING_SIZE,
        })
        .then(listItems => {
          setViewMode(listItems.length === 0 ? AppConstant.VIEW_MODE.empty : AppConstant.VIEW_MODE.list);
          setIsLoadMore(listItems.length === PAGING_SIZE);

          refactorFiles(prefixKey, listItems).then(result => {
            const listByDate = groupDataByDate(result);
            setFileList(listByDate);
            setDisplayItemTotal(listItems.length);
          });
        });
    }, AppConstant.DEBOUNCE_TIME);

    return () => clearTimeout(getFileTimeout);
  }, [prefixKey, searchValue, filterCondition]);

  return (
    <MainLayout>
      <MainSidebar>
        <AppDrawer open title={accountObjContent.manageFile} onClose={handleOpenHomePage}>
          <Stack px={2} spacing={0.5}>
            <FileTabButton
              isSelected={FILE_TAB.all === selectedTab}
              label={getCommonLang(LangConstant.TXT_ALL)}
              startIcon={<Apps />}
              onClick={handleSelectTabFunc(FILE_TAB.all)}
            />

            <FileTabButton
              isSelected={FILE_TAB.mine === selectedTab}
              label={getNSLang(LangConstant.NS_HOME_CONVERSATION, LangConstant.TXT_SENT)}
              startIcon={<ReplyIcon sx={{ transform: "scale(-1,1)" }} />}
              onClick={handleSelectTabFunc(FILE_TAB.mine)}
            />

            <FileTabButton
              isSelected={FILE_TAB.other === selectedTab}
              label={getLabel("TXT_SHARE_BY_OTHER")}
              startIcon={<ReplyIcon sx={{ transform: "scale(1,-1)" }} />}
              onClick={handleSelectTabFunc(FILE_TAB.other)}
            />
          </Stack>
        </AppDrawer>
      </MainSidebar>

      <MainContent className="align-items">
        <Container>
          <LimitContainer>
            <SearchBar onChange={handleSearch} placeholder={getCommonLang("TXT_FINDING_FILE")} />
          </LimitContainer>

          <LimitContainer component={Stack} direction="row">
            <FileFilter onChangeFileType={handleFilterByType} />
            <TimeFilter onChangeTime={handleFilterByTime} />
          </LimitContainer>

          <Scrollable onBottom={handleLoadMore}>
            {viewMode === AppConstant.VIEW_MODE.list && (
              <Stack component={List} gap={2} disablePadding paddingBottom={2}>
                {Object.keys(fileList).map(date => (
                  <ListFileByDate key={date} date={date} list={fileList[date]} isShowGroupName={true} />
                ))}
              </Stack>
            )}
            {viewMode === AppConstant.VIEW_MODE.fetching && <FileListLoading />}
          </Scrollable>
          {viewMode === AppConstant.VIEW_MODE.empty && (
            <LimitContainer margin="auto" pb={8}>
              <EmptyResult />
            </LimitContainer>
          )}
        </Container>
      </MainContent>
    </MainLayout>
  );
};

export default ManageFilePage;

const FILE_TAB = {
  all: 0,
  mine: 1,
  other: 2,
};
